import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo, useLayoutEffect, useMemo, useRef, useState } from "react";
import { PlanDetailButton } from "../plan-detail-button";

interface MealTitleWithDetailProps {
  meal: Meal;
  mealTime: MealTime;
  mealDay: string;
  targetDate?: string;
  mealIndex?: number;
  className?: string;
}

const TITLE_LINE_MAX_WIDTH_PX = 300;
const normalizeMealTitle = (value?: string | null): string => {
  if (!value) {
    return "";
  }

  return value
    .normalize("NFKC")
    .replace(/[\u00A0\u2000-\u200A\u202F\u205F\u3000]/g, " ")
    .replace(/[\u200B-\u200D\u2060\uFEFF]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

/**
 * Displays meal title with ellipsis after 2 lines and a detail button.
 * The button is positioned inline at the end and is always visible, with text truncated to accommodate it.
 */
export const MealTitleWithDetail = memo(function MealTitleWithDetail({
  meal,
  mealTime,
  mealDay,
  targetDate,
  mealIndex,
  className,
}: MealTitleWithDetailProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const buttonRef = useRef<HTMLSpanElement>(null);
  const [lines, setLines] = useState<{
    line1: string;
    line2: string;
    singleLine: boolean;
    line2Max: number;
  }>({
    line1: "",
    line2: "",
    singleLine: true,
    line2Max: 0,
  });

  const mealName = useMemo(() => normalizeMealTitle(meal.name), [meal.name]);

  useLayoutEffect(() => {
    const element = titleRef.current;
    if (!element) {
      return;
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    const measure = () => {
      const width = element.clientWidth;
      if (!width || !mealName) {
        setLines({ line1: mealName, line2: "", singleLine: true, line2Max: 0 });
        return;
      }

      const styles = window.getComputedStyle(element);
      ctx.font = `${styles.fontStyle} ${styles.fontVariant} ${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;

      const buttonWidth = buttonRef.current?.getBoundingClientRect().width ?? 24;
      const gap = 4;
      const maxLineWidth = Math.min(width, TITLE_LINE_MAX_WIDTH_PX);
      const line1Max = Math.max(0, maxLineWidth - buttonWidth - gap);
      const line2Max = Math.max(0, maxLineWidth - buttonWidth - gap);

      const words = mealName.split(/\s+/).filter(Boolean);
      const measureText = (text: string) => ctx.measureText(text).width;

      const truncatePlain = (text: string, maxWidth: number) => {
        if (!text) {
          return "";
        }

        if (measureText(text) <= maxWidth) {
          return text;
        }

        let truncated = "";
        for (let i = 0; i < text.length; i += 1) {
          const next = truncated + text[i];
          if (measureText(next) <= maxWidth) {
            truncated = next;
          } else {
            break;
          }
        }

        return truncated;
      };

      const truncateWithEllipsis = (text: string, maxWidth: number) => {
        if (!text) {
          return "...";
        }

        let truncated = text.trim();
        while (truncated.length > 0 && measureText(`${truncated}...`) > maxWidth) {
          truncated = truncated.slice(0, -1).trimEnd();
        }

        return truncated ? `${truncated}...` : "...";
      };

      let line1 = "";
      let nextIndex = 0;

      for (let index = 0; index < words.length; index += 1) {
        const candidate = line1 ? `${line1} ${words[index]}` : words[index];
        if (measureText(candidate) <= line1Max) {
          line1 = candidate;
          nextIndex = index + 1;
        } else {
          break;
        }
      }

      if (!line1 && words[0]) {
        line1 = truncatePlain(words[0], line1Max);
        nextIndex = 1;
      }

      if (nextIndex >= words.length) {
        setLines({ line1, line2: "", singleLine: true, line2Max });
        return;
      }

      const remainingText = words.slice(nextIndex).join(" ");
      const line2 = truncateWithEllipsis(remainingText, line2Max);

      setLines({ line1, line2, singleLine: false, line2Max });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [mealName]);

  return (
    <div className={cn("relative mb-6 min-h-[51px] w-full", className)}>
      <h3
        ref={titleRef}
        className={cn(
          textVariants.title3,
          "mx-auto w-full text-center text-xl font-bold capitalize",
        )}
        style={{ maxWidth: `${TITLE_LINE_MAX_WIDTH_PX}px` }}
      >
        {lines.singleLine ? (
          <span className="inline-flex items-center justify-center gap-1">
            <span>{lines.line1}</span>
            <span className="flex items-center justify-center" ref={buttonRef}>
              <PlanDetailButton
                meal={meal}
                mealTime={mealTime}
                mealDay={mealDay}
                targetDate={targetDate}
                mealIndex={mealIndex}
                classNames={{
                  button: "!h-6 !w-6 !p-0",
                  icon: "fill-screen-gs-1",
                }}
              />
            </span>
          </span>
        ) : (
          <span className="flex flex-col items-center">
            <span className="w-full whitespace-nowrap">{lines.line1}</span>
            <span className="inline-flex w-full items-center justify-center gap-1 whitespace-nowrap">
              <span
                className="inline-block truncate"
                style={lines.line2Max ? { maxWidth: `${lines.line2Max}px` } : undefined}
              >
                {lines.line2}
              </span>
              <span className="flex items-center justify-center" ref={buttonRef}>
                <PlanDetailButton
                  meal={meal}
                  mealTime={mealTime}
                  mealDay={mealDay}
                  targetDate={targetDate}
                  mealIndex={mealIndex}
                  classNames={{
                    button: "!h-6 !w-6 !p-0",
                    icon: "fill-screen-gs-1",
                  }}
                />
              </span>
            </span>
          </span>
        )}
      </h3>
    </div>
  );
});
